import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { BehaviorSubject, Observable, catchError, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { CommonService } from './common.service';
import { SnackbarService } from './snackbar.service';
import { UserService } from './user-service.service';

const AUTH_ENDPOINT = {
  register: 'users/register',
  verifyOTP: 'users/verify-email',
  resendOTP: 'users/resend-otp',
  login: 'auth/login',
  logout: 'auth/logout',
  forgotPassword: 'auth/request-password-reset',
  resetPassword: 'auth/reset-password',
  ssoLogin: 'sso/auth-url',
};

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private TOKEN_KEY = 'token';
  private USER_KEY = 'user';
  private USER_ROLE_KEY = 'user_role';

  private isLoggedInSubject = new BehaviorSubject<boolean>(
    !!sessionStorage.getItem(this.TOKEN_KEY)
  );
  public isLoggedIn$ = this.isLoggedInSubject.asObservable();

  public isAdminLoggedInSubject = new BehaviorSubject<boolean>(
    !!sessionStorage.getItem(this.TOKEN_KEY)
  );
  public isAdminLoggedIn$ = this.isAdminLoggedInSubject.asObservable();

  constructor(
    private apiService: ApiService,
    private router: Router,
    private commonService: CommonService,
    private snackbarService: SnackbarService,
    private userService: UserService
  ) { }

  /**
   * Central method to check if user is authenticated
   * Returns the current authentication state and updates the subject
   */
  checkAuthState(): boolean {
    const isLoggedIn = !!sessionStorage.getItem(this.TOKEN_KEY);
    const userRole = sessionStorage.getItem(this.USER_ROLE_KEY);
    this.isLoggedInSubject.next(isLoggedIn);
    this.isAdminLoggedInSubject.next(isLoggedIn && userRole === 'admin');
    return isLoggedIn;
  }

  signup(data: Record<string, any>): Observable<any> {
    return this.apiService.post(AUTH_ENDPOINT.register, data);
  }

  verifyOTP(data: any): Observable<any> {
    return this.apiService.post(AUTH_ENDPOINT.verifyOTP, data);
  }

  resendOTP(data: any): Observable<any> {
    return this.apiService.post(AUTH_ENDPOINT.resendOTP, data);
  }

  login(data: any, options: any = {}): Observable<any> {
    return this.apiService
      .post(
        AUTH_ENDPOINT.login,
        {
          ...data,
          lat: this.commonService.latitude,
          lng: this.commonService.longitude,
        },
        { ...options }
      )
      .pipe(
        tap((res: any) => {
          if (res.data?.accessToken) {
            this.setLoginToken(res.data.accessToken);
            this.userService.setUserDetails(res.data.user);
            this.checkAuthState();
            const userRole =
              options?.['headers']?.['login-portal'] === 'admin'
                ? 'admin'
                : res.data.user?.userType ?? 'public';
            this.setUserRole(userRole);
          }
        })
      );
  }

  logout(): Observable<any> {
    const message = 'You have been successfully logged out.';
    return this.apiService
      .post(AUTH_ENDPOINT.logout, {
        lat: this.commonService.latitude,
        lng: this.commonService.longitude,
      })
      .pipe(
        tap((res: any) => {
          if (res.success) {
            this.snackbarService.showSuccess(message);
            this.autoLogout();
          }
        }),
        catchError((err) => {
          // Even if API call fails, clear session and show success message
          this.autoLogout();
          this.snackbarService.showSuccess(err.error.message || message);
          return of({ success: true, message: 'Logged out successfully' });
        })
      );
  }

  /**
   * Handles automatic logout when token expires or authentication fails
   */
  autoLogout(): void {
    this.clearSession();
    this.isLoggedInSubject.next(false);
    this.isAdminLoggedInSubject.next(false);
    this.redirectToLogin();
  }

  forgotPassword(data: any): Observable<any> {
    return this.apiService.post(AUTH_ENDPOINT.forgotPassword, data);
  }

  resetPassword(data: any): Observable<any> {
    return this.apiService.post(AUTH_ENDPOINT.resetPassword, data);
  }

  clearSession(): void {
    sessionStorage.removeItem(this.TOKEN_KEY);
    sessionStorage.removeItem(this.USER_KEY);
    sessionStorage.removeItem(this.USER_ROLE_KEY);
  }

  ssoLogin({
    provider,
    frontendRedirectUri = location.href,
    loginType,
    loginPortal,
  }: {
    provider: string;
    frontendRedirectUri?: string;
    loginType: string;
    loginPortal: string;
  }): Observable<any> {
    return this.apiService.get(AUTH_ENDPOINT.ssoLogin, {
      params: {
        provider,
        lat: this.commonService.latitude,
        lng: this.commonService.longitude,
        loginType,
        loginPortal,
        frontendRedirectUri,
      },
    });
  }

  setLoginToken(token: string): void {
    sessionStorage.setItem(this.TOKEN_KEY, token);
  }

  getLoginToken(): string {
    return sessionStorage.getItem(this.TOKEN_KEY) || '';
  }

  setUserRole(role: string): void {
    sessionStorage.setItem(this.USER_ROLE_KEY, role);
  }

  getUserRole(): string {
    return sessionStorage.getItem(this.USER_ROLE_KEY) || 'public';
  }

  processSSOToken(token: string): void {
    const { data } = JSON.parse(atob(token));
    this.setLoginToken(data.accessToken);
    this.userService.setUserDetails(data.user);
    this.setUserRole(data.user?.userType || 'public');
  }

  redirectToLogin(): void {
    const wasAdminLoggedIn = window.location.href.includes('/admin');
    this.router.navigate([wasAdminLoggedIn ? '/admin/login' : '/auth/login']);
  }
}
